/**
 * The code someone types to join a workspace.
 *
 * Workspaces used to be picked from a list the registration screen fetched
 * unauthenticated, which meant the name of every workspace on the deployment
 * was readable by anyone who opened the signup page. A code inverts that: a
 * workspace is reachable only by someone who was given the way in, and
 * nothing about it — that it exists, what it is called — is discoverable
 * without one.
 */
/**
 * Crockford's base32: no `I`, `L`, `O` or `U`, so there is no pair a person
 * can confuse when reading a code off a screen or a whiteboard, and `U` is
 * left out so a random code cannot spell something unfortunate.
 */
export const JOIN_CODE_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
/** 12 symbols of a 32-letter alphabet — 60 bits, far past guessing. */
export const JOIN_CODE_LENGTH = 12;
/** Written in threes: `A1B2-C3D4-E5F6`. */
const GROUP_SIZE = 4;
/**
 * The canonical form of whatever was typed.
 *
 * Hyphens and spaces are decoration, so they are dropped rather than
 * rejected — the code is displayed grouped and people paste it back that way.
 * The letters Crockford's alphabet leaves out are folded onto the digits they
 * are mistaken for, so a code read aloud as "oh" and typed as `O` still
 * matches the `0` that was generated.
 */
export function normalizeJoinCode(input) {
    return input
        .toUpperCase()
        .replace(/[^0-9A-Z]/g, '')
        .replace(/O/g, '0')
        .replace(/[IL]/g, '1');
}
export function isValidJoinCode(code) {
    if (code.length !== JOIN_CODE_LENGTH)
        return false;
    return [...code].every((symbol) => JOIN_CODE_ALPHABET.includes(symbol));
}
/** `A1B2C3D4E5F6` -> `A1B2-C3D4-E5F6`, for anywhere a code is shown. */
export function formatJoinCode(code) {
    const groups = [];
    for (let at = 0; at < code.length; at += GROUP_SIZE) {
        groups.push(code.slice(at, at + GROUP_SIZE));
    }
    return groups.join('-');
}
